<!--
  Dashboard section icon — replaces the old per-tenant SVG files (<TenantImage>).

  Renders a Material Symbol in a rounded, brand-tinted square, matching the
  Flutter app's dashboard tiles. Colour comes entirely from the --brand-color
  CSS variable (set per tenant from $primary-color), so there are NO per-tenant
  image files to manage — onboarding a club is just a colour change.

  Usage:  <SectionIcon name="boot" />
-->
<template>
  <span class="section-icon" :style="sizeVars" role="img" :aria-label="ariaLabel">
    <span class="material-symbols-outlined" aria-hidden="true">{{ icon }}</span>
  </span>
</template>

<script>
import { iconForSection } from "@/config/dashboardIcons";

export default {
  name: "SectionIcon",
  props: {
    // Section key (e.g. 'boot', 'teams', 'nutrition') — same keys the old
    // TenantImage image-name used, so this is a drop-in replacement.
    name: {
      type: String,
      required: true,
    },
    // Size of the rounded container in px (icon scales with it).
    // Scaled up for the larger web dashboard cards.
    size: {
      type: [Number, String],
      default: 72,
    },
  },
  computed: {
    icon() {
      return iconForSection(this.name);
    },
    ariaLabel() {
      return `${this.name} icon`;
    },
    sizeVars() {
      const box = parseInt(this.size, 10) || 56;
      return {
        "--icon-box": `${box}px`,
        "--icon-glyph": `${Math.round(box * 0.55)}px`,
        "--icon-radius": `${Math.round(box * 0.27)}px`,
      };
    },
  },
};
</script>

<style scoped>
.section-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: var(--icon-box, 56px);
  height: var(--icon-box, 56px);
  border-radius: var(--icon-radius, 15px);
  /* 12% tint of the tenant brand colour behind the glyph (Flutter look).
     color-mix keeps it in sync with --brand-color automatically. */
  background: color-mix(in srgb, var(--brand-color, #eb2144) 12%, transparent);
  /* Fallback for browsers without color-mix: a neutral tint. */
  background: rgba(0, 0, 0, 0.05);
  background: color-mix(in srgb, var(--brand-color, #eb2144) 12%, transparent);
}

.section-icon .material-symbols-outlined {
  color: var(--brand-color, #eb2144);
  font-size: var(--icon-glyph, 30px);
  line-height: 1;
  /* Match Flutter's outlined weight/roundness. */
  font-variation-settings: "FILL" 0, "wght" 400, "GRAD" 0, "opsz" 24;
}
</style>
